import { z } from 'zod';
import { LOCALE_KEY_PATTERN } from '../content/studio';

/**
 * Curated BCP-47 language tags for Web-Studio (configurator picker + validation).
 * Fixed catalog of 12 — operators enable a subset per project.
 */
export type PlatformLocaleDefinition = {
  /** Lowercase BCP-47 tag */
  id: string;
  /** Endonym shown in the configurator list */
  nativeLabel: string;
  /** Compact label for workspace pills */
  pillLabel: string;
};

/** Locked v4.1.1 hospitality catalog (DACH/EU + global tourism). */
export const PLATFORM_LOCALE_CATALOG: readonly PlatformLocaleDefinition[] = [
  { id: 'de', nativeLabel: 'Deutsch', pillLabel: 'DE' },
  { id: 'en', nativeLabel: 'English (US)', pillLabel: 'EN' },
  { id: 'es', nativeLabel: 'Español', pillLabel: 'ES' },
  { id: 'fr', nativeLabel: 'Français', pillLabel: 'FR' },
  { id: 'it', nativeLabel: 'Italiano', pillLabel: 'IT' },
  { id: 'nl', nativeLabel: 'Nederlands', pillLabel: 'NL' },
  { id: 'pl', nativeLabel: 'Polski', pillLabel: 'PL' },
  { id: 'tr', nativeLabel: 'Türkçe', pillLabel: 'TR' },
  { id: 'pt', nativeLabel: 'Português', pillLabel: 'PT' },
  { id: 'ja', nativeLabel: '日本語', pillLabel: 'JA' },
  { id: 'zh', nativeLabel: '中文（简体）', pillLabel: 'ZH' },
  { id: 'vi', nativeLabel: 'Tiếng Việt', pillLabel: 'VI' },
] as const;

export const PLATFORM_LOCALE_CATALOG_SIZE = PLATFORM_LOCALE_CATALOG.length;

export const PLATFORM_LOCALE_CATALOG_IDS: readonly string[] = PLATFORM_LOCALE_CATALOG.map(
  (entry) => entry.id,
);

const catalogById = new Map(PLATFORM_LOCALE_CATALOG.map((entry) => [entry.id, entry]));

/** Root URL locale for platform chrome (studio / marketplace unprefixed paths). */
export const DEFAULT_PLATFORM_LOCALE = 'en' as const;

/**
 * Default locale for **customer sites** (project `defaultLocale`).
 * Germany-market gastronomy — unprefixed public paths are DE.
 */
export const DEFAULT_SITE_LOCALE = 'de' as const;

/** Suggested initial supported locales when opening Step 3 (site default + English). */
export const PLATFORM_LOCALE_ONBOARDING_DEFAULTS: readonly string[] = [
  DEFAULT_SITE_LOCALE,
  'en',
] as const;

/**
 * Fallback read order when a content key is *missing* for the active locale.
 * An explicit empty string (`""`) is intentional and must not use this order —
 * see `pickLocalized` in core-components.
 */
export const PLATFORM_LOCALE_FALLBACK_ORDER: readonly string[] = [
  DEFAULT_SITE_LOCALE,
  'en',
  ...PLATFORM_LOCALE_CATALOG_IDS.filter((id) => id !== DEFAULT_SITE_LOCALE && id !== 'en'),
];

export function getPlatformLocaleDefinition(
  localeId: string,
): PlatformLocaleDefinition | undefined {
  return catalogById.get(localeId.trim().toLowerCase());
}

const languageDisplayNamesByUiLocale = new Map<string, Intl.DisplayNames>();

function getLanguageDisplayNames(uiLocale: string): Intl.DisplayNames {
  const normalizedUiLocale = uiLocale.trim().toLowerCase();
  let display = languageDisplayNamesByUiLocale.get(normalizedUiLocale);
  if (!display) {
    display = new Intl.DisplayNames([normalizedUiLocale], { type: 'language' });
    languageDisplayNamesByUiLocale.set(normalizedUiLocale, display);
  }
  return display;
}

/** Language name in the studio UI locale (`Intl.DisplayNames`). */
export function formatPlatformLocaleDisplayLabel(localeId: string, uiLocale: string): string {
  const id = localeId.trim().toLowerCase();
  if (!isPlatformCatalogLocale(id)) {
    return id;
  }
  return getLanguageDisplayNames(uiLocale).of(id) ?? id;
}

/** UI-locale name + pill code — configurator dropdowns and add-locale selects. */
export function formatPlatformLocaleOption(localeId: string, uiLocale: string): string {
  const entry = getPlatformLocaleDefinition(localeId);
  if (!entry) {
    return localeId;
  }
  return `${formatPlatformLocaleDisplayLabel(localeId, uiLocale)} (${entry.pillLabel})`;
}

export function isPlatformCatalogLocale(localeId: string): boolean {
  const normalized = localeId.trim().toLowerCase();
  return LOCALE_KEY_PATTERN.test(normalized) && catalogById.has(normalized);
}

export function normalizeLocaleList(locales: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of locales) {
    const id = raw.trim().toLowerCase();
    if (!LOCALE_KEY_PATTERN.test(id) || seen.has(id)) {
      continue;
    }
    seen.add(id);
    out.push(id);
  }
  return out;
}

/** Keeps only locales present in the catalog, preserving input order. */
export function filterToPlatformCatalog(locales: string[]): string[] {
  return normalizeLocaleList(locales).filter(isPlatformCatalogLocale);
}

const localeKeySchema = z
  .string()
  .regex(LOCALE_KEY_PATTERN)
  .refine(isPlatformCatalogLocale, { message: 'Locale must be in PLATFORM_LOCALE_CATALOG' });

export const ProjectLocalePolicySchema = z
  .object({
    defaultLocale: localeKeySchema,
    supportedLocales: z.array(localeKeySchema).min(1),
  })
  .superRefine((policy, ctx) => {
    const supported = normalizeLocaleList(policy.supportedLocales);
    if (!supported.includes(policy.defaultLocale)) {
      ctx.addIssue({
        code: 'custom',
        message: 'defaultLocale must be included in supportedLocales',
        path: ['defaultLocale'],
      });
    }
  });

export type ProjectLocalePolicy = z.infer<typeof ProjectLocalePolicySchema>;

export function parseProjectLocalePolicy(
  configuration: { defaultLocale: string; supportedLocales: string[] },
): ProjectLocalePolicy {
  return ProjectLocalePolicySchema.parse(configuration);
}

/**
 * Build a localized field map for bootstrap: uses per-locale values when provided,
 * otherwise copies the default locale string (studio translates later).
 */
export function buildLocalizedRecord(
  defaultLocale: string,
  supportedLocales: string[],
  valuesByLocale: Partial<Record<string, string>>,
): Record<string, string> {
  const locales = filterToPlatformCatalog(supportedLocales);
  const fallback =
    valuesByLocale[defaultLocale] ??
    locales.map((id) => valuesByLocale[id]).find((v) => typeof v === 'string' && v.length > 0) ??
    '';
  const record: Record<string, string> = {};
  for (const id of locales) {
    const value = valuesByLocale[id];
    record[id] = typeof value === 'string' && value.length > 0 ? value : fallback;
  }
  return record;
}
